import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import TaskAltTwoToneIcon from '@mui/icons-material/TaskAltTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { cloneDeep } from 'lodash';
import {
  BillingInterval,
  CurrentPlan,
  Feature,
  Plan,
} from './types';
import FeaturesPreventingDowngradePopover from './features-preventing-downgrade-popover';
import Button from '../components/button';
import linkTo, { templates } from '../utils/linkTo';
import { useApp } from '../hooks/use-app';

interface Props {
  plan: Plan;
  currentPlan: CurrentPlan;
  minConversionLicenses: number;
  onSelectPlan: (plan: Plan, billingInterval: BillingInterval) => void;
}

const PlanCard: React.FC<Props> = ({
  plan, currentPlan, minConversionLicenses, onSelectPlan,
}) => {
  const { globals } = useApp();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(plan.prices.year ? 'year' : 'month');
  const [featuresPopoverAnchor, setFeaturesPopoverAnchor] = useState<Element | null>(null);
  const [featuresPreventingDowngrade, setFeaturesPreventingDowngrade] = useState<Feature[]>([]);
  const isPlanSameAsCurrentPlan = plan.publicSlug === currentPlan.publicSlug;
  const areUpgradeRanksTheSame = plan.upgradeRank === currentPlan.upgradeRank;
  const validComparisonCard = currentPlan?.comparisonData?.cardPrice;
  const isPlanCurrentWithComparisonData = validComparisonCard && areUpgradeRanksTheSame;
  const cardIncludesFeatures = isPlanCurrentWithComparisonData
    ? validComparisonCard.includesFeatures
    : cloneDeep(plan).includesFeatures;

  useEffect(() => {
    const features: Feature[] = [];
    if (plan.maxForms && plan.maxForms < currentPlan.usage.formCount) {
      features.push({
        name: 'Custom Apps',
        type: 'amount',
        current: currentPlan.usage.formCount,
        plan: plan.maxForms,
      });
    }
    if (plan.maxUsers && plan.maxUsers < currentPlan.usage.membershipCount) {
      features.push({
        name: 'Users',
        type: 'amount',
        current: currentPlan.usage.membershipCount,
        plan: plan.maxUsers,
      });
    }
    if (plan.mediaDataQuota < currentPlan.usage.mediaDataUsage) {
      features.push({
        name: 'Storage',
        type: 'amount',
        current: `${currentPlan.usage.mediaDataUsage / 1073741824} GB`,
        plan: `${plan.mediaDataQuota / 1073741824} GB`,
      });
    }
    if (!plan.customRolesEnabled && currentPlan.usage.usingCustomRoles) {
      features.push({
        name: 'Custom Roles',
        type: 'flag',
        current: currentPlan.usage.usingCustomRoles,
        plan: plan.customRolesEnabled,
      });
    }
    if (!plan.repeatablesEnabled && currentPlan.usage.usingRepeatables) {
      features.push({
        name: 'Repeatables',
        type: 'flag',
        current: currentPlan.usage.usingRepeatables,
        plan: plan.repeatablesEnabled,
      });
    }
    if (!plan.signaturesEnabled && currentPlan.usage.usingSignatures) {
      features.push({
        name: 'Signatures',
        type: 'flag',
        current: currentPlan.usage.usingSignatures,
        plan: plan.signaturesEnabled,
      });
    }
    if (!plan.dataSharesEnabled && currentPlan.usage.usingDataShares) {
      features.push({
        name: 'Data Shares',
        type: 'flag',
        current: currentPlan.usage.usingDataShares,
        plan: plan.dataSharesEnabled,
      });
    }
    if (!plan.apiAccessEnabled && currentPlan.usage.usingApi) {
      features.push({
        name: 'API Access',
        type: 'flag',
        current: currentPlan.usage.usingApi,
        plan: plan.apiAccessEnabled,
      });
    }
    if (!plan.barcodesEnabled && currentPlan.usage.usingBarcodes) {
      features.push({
        name: 'Barcodes',
        type: 'flag',
        current: currentPlan.usage.usingBarcodes,
        plan: plan.barcodesEnabled,
      });
    }
    if (!plan.webhooksEnabled && currentPlan.usage.usingWebhooks) {
      features.push({
        name: 'Webhooks',
        type: 'flag',
        current: currentPlan.usage.usingWebhooks,
        plan: plan.webhooksEnabled,
      });
    }
    if (!plan.recordLinksEnabled && currentPlan.usage.usingRecordLinks) {
      features.push({
        name: 'Record Links',
        type: 'flag',
        current: currentPlan.usage.usingRecordLinks,
        plan: plan.recordLinksEnabled,
      });
    }
    if (!plan.videosEnabled && currentPlan.usage.usingVideoFields) {
      features.push({
        name: 'Video Fields',
        type: 'flag',
        current: currentPlan.usage.usingVideoFields,
        plan: plan.videosEnabled,
      });
    }
    if (!plan.audioEnabled && currentPlan.usage.usingAudioFields) {
      features.push({
        name: 'Audio Fields',
        type: 'flag',
        current: currentPlan.usage.usingAudioFields,
        plan: plan.audioEnabled,
      });
    }
    if (!plan.layersEnabled && currentPlan.usage.usingLayers) {
      features.push({
        name: 'Map Layers',
        type: 'flag',
        current: currentPlan.usage.usingLayers,
        plan: plan.layersEnabled,
      });
    }

    setFeaturesPreventingDowngrade(features);
  }, []);

  const planButton = () => {
    let disabled = false;
    let text = 'Select Plan';
    let icon: React.ReactNode | null = null;
    let handleClick = handleClickSelectPlan;
    let href: string | undefined;

    const isCurrentBillingIntervalSelected = billingInterval === currentPlan.billingInterval;
    const isEnterprisePlan = plan.publicSlug === 'enterprise';

    if (
      (isPlanSameAsCurrentPlan && isCurrentBillingIntervalSelected)
      || isPlanCurrentWithComparisonData
    ) {
      disabled = true;
      text = 'Current Plan';
    } else if (isEnterprisePlan) {
      text = 'Contact Us';
      handleClick = handleClickContactUs;
    } else if (featuresPreventingDowngrade.length > 0) {
      disabled = true;
      text = 'Unavailable';
      icon = <InfoTwoToneIcon />;
    } else if (currentPlan.upgradeRank !== null) {
      const isTryingToDowngradePlan = plan.upgradeRank < currentPlan.upgradeRank;
      const isTryingToDowngradeBillingInterval = isPlanSameAsCurrentPlan && billingInterval !== currentPlan.billingInterval && currentPlan.billingInterval === 'year';
      text = isTryingToDowngradePlan || isTryingToDowngradeBillingInterval ? 'Contact Us' : 'Upgrade';
      if (text === 'Contact Us') {
        href = linkTo.mail.billing({
          template: templates.billing.changePlan,
          orgId: globals.organization.id,
          userId: globals.user.id,
        });
      }
    }

    return (
      <Button
        variant="contained"
        size="small"
        disabled={disabled}
        {...href ? { href } : {
          onClick: handleClick,
        }}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        endIcon={icon}
        // @ts-ignore
        component={disabled ? 'div' : 'button'}
      >
        {text}
      </Button>
    );
  };

  const handleClickSelectPlan = () => {
    onSelectPlan(plan, billingInterval);
  };

  const handleClickContactUs = () => {
    window.open('https://www.fulcrumapp.com/enterprise', '_blank');
  };

  const handleBillingIntervalChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setBillingInterval(event.target.checked ? 'year' : 'month');
  };

  const handleMouseOver: React.MouseEventHandler = (event) => {
    if (featuresPreventingDowngrade.length > 0) {
      setFeaturesPopoverAnchor(event.currentTarget);
    }
  };

  const handleMouseLeave: React.MouseEventHandler = () => {
    setFeaturesPopoverAnchor(null);
  };

  const safePriceAmountCopy = (amount: number | string | undefined) => {
    if (isPlanCurrentWithComparisonData) {
      return validComparisonCard.amount;
    }
    if (typeof amount === 'number') {
      return `$${(amount / 100)}`;
    }
    return '$0';
  };

  const price = plan.prices[billingInterval];
  const isYearBillingInterval = billingInterval === 'year';
  const minUsersText = plan.name === 'Enterprise' ? '' : `Minimum ${minConversionLicenses} users`;
  const billingIntervalText = isPlanCurrentWithComparisonData
    ? validComparisonCard.description
    : price?.description;
  const subText = minUsersText ? `${minUsersText}, ${billingIntervalText}` : billingIntervalText;

  return (
    <Card>
      <CardContent sx={{
        display: 'flex', flexDirection: 'column', height: '100%', gap: 3,
      }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2">{isPlanCurrentWithComparisonData ? currentPlan.name : plan.name}</Typography>
          {(price?.discount || 0) > 0 && (
            <Chip label={`Save ${price?.discount}%`} size="small" color="success" />
          )}
        </Box>
        <Typography variant="body1" sx={{ height: '25px' }}>
          {plan.name === 'Enterprise' ? '' : subText }
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
          {price?.amount ? (
            <>
              <Typography variant="h1">{safePriceAmountCopy(price?.amount)}</Typography>
              {!isPlanCurrentWithComparisonData
                ? <Typography variant="h3">{`per ${price?.per}`}</Typography>
                : <Typography variant="h3">{` ${validComparisonCard.per}`}</Typography>}
            </>
          ) : (
            <Typography variant="h1">Let&apos;s Talk</Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignContent: 'center',
            gap: 3,
          }}
        >
          {planButton()}
          {!isPlanCurrentWithComparisonData
            ? (
              <FeaturesPreventingDowngradePopover
                anchor={featuresPopoverAnchor}
                handleClose={handleMouseLeave}
                features={featuresPreventingDowngrade}
              />
            )
            : <></>}
          {plan.prices.month && plan.prices.year && (
            <FormGroup sx={{ flexDirection: 'row' }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={isYearBillingInterval}
                    onChange={handleBillingIntervalChange}
                    color="secondary"
                    size="small"
                  />
                )}
                label="Pay Annually"
              />
            </FormGroup>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 2, height: '100%',
          }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <strong>{plan.includesText}</strong>
              <List dense>
                {cardIncludesFeatures?.map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemIcon sx={{
                      minWidth: 0, marginRight: 1, marginTop: '4px', alignSelf: 'flex-start',
                    }}
                    >
                      <TaskAltTwoToneIcon sx={{ color: '#87D30F' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              {plan.moreFeatures && (
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <a
                    href="https://www.fulcrumapp.com/pricing/#detailed_feature_list"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    And many more
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                      <ExitToAppIcon fontSize="small" />
                    </Box>
                  </a>
                </Typography>
              )}
            </Box>
            <Typography variant="body2" component="div">
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 2, mb: 1,
              }}
              >
                <Box>
                  Data shares:
                  {' '}
                  <strong>
                    {plan.name === 'Enterprise'
                      ? 'Unlimited'
                      : plan.dataSharesLimit}
                  </strong>
                </Box>
                <Box>
                  Support:
                  {' '}
                  <strong>
                    {plan.name === 'Professional' ? 'Standard email and in-app' : 'Priority email, in-app and phone'}
                  </strong>
                </Box>
                <Box>
                  APIs, webhooks:
                  {' '}
                  <strong>
                    {plan.name === 'Professional'
                      ? 'Unavailable'
                      : 'Included'}
                  </strong>
                </Box>
              </Box>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlanCard;
